import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Text,
  FormErrorMessage,
  Box,
  List,
  ListItem,
} from "@chakra-ui/react";
import { ChevronDown, Search } from "lucide-react";
import PRESCRIPTION_TYPES from "@/constants/medicines_sorted.json";
import { Prescription } from "@/types/appointment";

interface AddPrescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prescription: { type: string; description: string; notes: string }) => void;
  editingPrescription: Prescription | null;
}


const AddPrescriptionModal: React.FC<AddPrescriptionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingPrescription,
}) => {
  const [type, setType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [description, setDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [errors, setErrors] = useState<{
    type?: string;
    description?: string;
  }>({});

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter prescription types based on search query
  const filteredTypes = PRESCRIPTION_TYPES.filter((prescriptionType) =>
    prescriptionType.medicine_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (editingPrescription) {
      setType(editingPrescription.type);
      setSearchQuery(editingPrescription.type);
      setDescription(editingPrescription.description);
      setNotes(editingPrescription.notes ?? "");
    } else {
      setType("");
      setSearchQuery("");
      setDescription("");
      setNotes("");
    }
    setErrors({});
    setShowDropdown(false);
  }, [editingPrescription, isOpen]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validateForm = () => {
    const newErrors: { type?: string; description?: string } = {};

    if (!type.trim()) {
      newErrors.type = "Prescription type is required";
    }

    if (!description.trim()) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    onSave({
      type: type.trim(),
      description: description.trim(),
      notes: notes.trim(),
    });

    handleClose();
  };

  const handleClose = () => {
    setType("");
    setSearchQuery("");
    setDescription("");
    setNotes("");
    setErrors({});
    setShowDropdown(false);
    onClose();
  };

  const handleTypeSelect = (selectedType: string) => {
    setType(selectedType);
    setSearchQuery(selectedType);
    setShowDropdown(false);
    clearError("type");
    
    // Auto-fill description from the selected prescription type
    const selectedMedicine = PRESCRIPTION_TYPES.find(
      (med) => med.medicine_name === selectedType
    );
    
    if (selectedMedicine && selectedMedicine.description) {
      setDescription(selectedMedicine.description);
      clearError("description");
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setType(value); // Allow custom types
    setShowDropdown(true);
    if (value.trim()) {
      clearError("type");
    }
  };

  const clearError = (field: "type" | "description") => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(1px)"   />
      <ModalContent borderRadius={0}>
        <ModalHeader fontSize={{ base: "lg", md: "xl" }}>
          {editingPrescription ? "Edit Prescription" : "Add Prescription"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isInvalid={!!errors.type}>
              <FormLabel fontSize={{ base: "sm", md: "md" }} color="#0A1B39">
                Prescription Type{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              
              <Box position="relative" ref={dropdownRef}>
                <Box position="relative">
                  <Input
                    ref={inputRef}
                    placeholder="Search or type prescription type..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    size={{ base: "sm", md: "md" }}
                    fontSize={{ base: "sm", md: "md" }}
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    pr="40px"
                  />
                  <Box
                    position="absolute"
                    right="2"
                    top="50%"
                    transform="translateY(-50%)"
                    display="flex"
                    alignItems="center"
                    gap={1}
                    pointerEvents="none"
                  >
                    <Search size={14} color="#9CA3AF" />
                    <ChevronDown size={14} color="#9CA3AF" />
                  </Box>
                </Box>

                {showDropdown && (
                  <Box
                    position="absolute"
                    top="100%"
                    left="0"
                    right="0"
                    zIndex="1000"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    mt={1}
                    maxH="250px"
                    overflowY="auto"
                    boxShadow="lg"
                  >
                    {filteredTypes.length > 0 ? (
                      <List>
                        {filteredTypes.map((prescriptionType, index) => (
                          <ListItem
                            key={index}
                            p={3}
                            cursor="pointer"
                            fontSize={{ base: "sm", md: "md" }}
                            _hover={{ bg: "#fbeaea" }}
                            onClick={() => handleTypeSelect(prescriptionType.medicine_name)}
                            borderBottom={
                              index < filteredTypes.length - 1 ? "1px solid" : "none"
                            }
                            borderColor="gray.100"
                          >
                            {prescriptionType.medicine_name}
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box p={3} textAlign="center" color="gray.500" fontSize="sm">
                        {searchQuery ? (
                          <>
                            No matches found. Press Enter to use{" "}
                            <Text as="span" fontWeight="600" color="#DC3611">
                              "{searchQuery}"
                            </Text>
                          </>
                        ) : (
                          "Start typing to search..."
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              
              <FormErrorMessage fontSize="xs">{errors.type}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.description}>
              <FormLabel fontSize={{ base: "sm", md: "md" }} color="#0A1B39">
                Description{" "}
                <Text as="span" color="red.500">
                  *
                </Text>
              </FormLabel>
              <Textarea
                placeholder="Describe the prescription details, dosage, frequency..."
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  clearError("description");
                }}
                size={{ base: "sm", md: "md" }}
                fontSize={{ base: "sm", md: "md" }}
                bg="white"
                borderRadius="md"
                borderColor="gray.200"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{
                  borderColor: "#DC3611",
                  boxShadow: "0 0 0 1px #DC3611",
                }}
                rows={4}
              />
              <FormErrorMessage fontSize="xs">{errors.description}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel fontSize={{ base: "sm", md: "md" }} color="#0A1B39">
                Additional Notes
              </FormLabel>
              <Textarea
                placeholder="Any additional notes or instructions..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                size={{ base: "sm", md: "md" }}
                fontSize={{ base: "sm", md: "md" }}
                bg="white"
                borderRadius="md"
                borderColor="gray.200"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{
                  borderColor: "#DC3611",
                  boxShadow: "0 0 0 1px #DC3611",
                }}
                rows={3}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            size={{ base: "sm", md: "md" }}
            fontSize={{ base: "sm", md: "md" }}
          >
            Cancel
          </Button>
          <Button
            bg="#DC3611"
            _hover={{ bg: "#bf3c1fff" }}
            color="white"
            onClick={handleSave}
            size={{ base: "sm", md: "md" }}
            fontSize={{ base: "sm", md: "md" }}
          >
            {editingPrescription ? "Update" : "Add"} Prescription
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddPrescriptionModal;